import axios from "axios";
import React, {useState} from "react";
import styled from "styled-components";
import {Dialog, Menu, Transition} from "@headlessui/react";
import {NavHeader} from "../../components/NavHeader";
import {Sidebar} from "../../components/Sidebar";
import Feedback from "../../components/Feedback";
import {useQuery, useMutation, useQueryClient} from "react-query";
import {getDate} from "../../utils/utils";
import {useRoles} from "../../hooks/useRoles";
import Filter from "../../components/Filter";
import AppLayout from "../../layouts/AdminLayout";
import {EmptyState} from "../../components/EmptyState";
import toast from "react-hot-toast";

export const Admin = () => {
  const queryClient = useQueryClient();
  const Baseurl = process.env.REACT_APP_BASEURL;
  const [searchFilter, setSearchFilter] = useState<any>();
  const getToken: any = localStorage.getItem("eftd-admin-token");
  const tokenObject: any = JSON.parse(getToken);
  const token = tokenObject?.data?.data?.token;
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
  const [modalState, setModalState] = useState("default");
  const [adminId, setAdminId] = useState("");
  const [adminEmail, setAdminEmail] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [openFeedback, setOpenFeedback] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [isSuperLoading, setSuperLoading] = useState(false);
  const [isResettingPass, setResettingPass] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [data, setData] = useState({
    name: "",
    role: "",
    email: "",
    phoneNumber: "",
    password: "",
  });
  const {isSuperAdmin} = useRoles();

  const getUser = async () => {
    const response = await axios.get(`${Baseurl}/api/admin/auth/all-admins`, {
      ...config,
      params: {...searchFilter},
    });
    return response?.data?.result;
  };

  const resetPassword = async (email: string) => {
    setResettingPass(true);
    try {
      await axios.post(
        `${Baseurl}/api/admin/auth/forgot-password`,
        {email},
        {
          ...config,
        }
      );
      toast.success("Password reset link sent to the admin.");
    } catch (error) {
      console.log(error);
      toast.error("Something went wrong!");
    } finally {
      setResettingPass(false);
      setIsModalOpen(false);
    }
  };

  const deleteUser = async (id: string) => {
    const response = await axios.delete(
      `${Baseurl}/api/admin/auth/${id}/`,
      config
    );
    return response?.data;
  };

  const deleteAdmin = useMutation(deleteUser, {
    onSettled: () => {
      setModalState("success");
      queryClient.invalidateQueries("ALLADMIN");
    },
  });

  const handleDelete = () => {
    deleteAdmin.mutate(adminId);
  };
  const {data: admins} = useQuery(["ALLADMIN", searchFilter], getUser);

  const handleChange = (e: any) => {
    setData((prevState) => ({
      ...prevState,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();

    const password = data.password || "";
    const errors: any[] = [];

    if (password.length < 8) {
      errors.push("Password must be at least 8 characters long");
    }

    if (!/[A-Z]/.test(password)) {
      errors.push("Password must contain at least one capital letter");
    }

    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
      errors.push("Password must contain at least one symbol");
    }

    // If there are validation errors, show them and prevent submission
    if (errors.length > 0) {
      errors.forEach((error) => toast.error(error));
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${Baseurl}/api/admin/auth/register`, data, config);
      toast.success("Admin created");
      //   setFeedback("success");
      //   setOpenFeedback(true);
      setIsModalOpen(false);
      queryClient.invalidateQueries("ALLADMIN");
    } catch (err: any) {
      const errorMessage = err.response.data.message;

      if (Array.isArray(errorMessage)) {
        toast.error(errorMessage[0]);
      } else if (typeof errorMessage === "string") {
        toast.error(errorMessage);
      } else {
        toast.error("An unexpected error occurred.");
      }
    } finally {
      setLoading(false);
    }
  };

  const createSuperAdmin = async (email: string) => {
    setSuperLoading(true);

    try {
      await axios.post(
        `${Baseurl}/api/admin/auth/createsuperadmin`,
        {email},
        config
      );
      toast.success("Super Admin created");
      //   setFeedback("success");
      //   setOpenFeedback(true);
      queryClient.invalidateQueries("ALLADMIN");
    } catch (err: any) {
      const errorMessage = err.response.data.message;

      if (Array.isArray(errorMessage)) {
        toast.error(errorMessage[0]);
      } else if (typeof errorMessage === "string") {
        toast.error(errorMessage);
      } else {
        toast.error("An unexpected error occurred.");
      }
    } finally {
      setSuperLoading(false);
    }
  };

  return (
    <AppLayout>
      <div className="p-5">
        <div className="flex items-center justify-between !px-9">
          <div>
            <h3 className="!text-[28px] !font-bold !text-[#272835]">
              Admin Users
            </h3>
            <p className="!text-sm !text-[#818898]">Manage all Admin users </p>
          </div>
          {isSuperAdmin && (
            <CreateAdmin
              onClick={() => {
                setIsModalOpen(true);
                setModalState("create");
              }}
            >
              Create new admin
            </CreateAdmin>
          )}
        </div>

        {isLoading ? (
          <div className="py-10 text-center text-gray-400 text-sm">
            Loading...
          </div>
        ) : admins?.length > 0 ? (
          <HistoryList>
            <Listdiv>
              <ListAddress className="date">Date</ListAddress>
              <ListAddress className="max-w-[150px]">Name</ListAddress>
              <ListAddress className="email max-w-[240px] text-wrap">
                Email Address
              </ListAddress>
              <ListAddress className="phone">Phone Number</ListAddress>
              <ListAddress className="role">Role</ListAddress>
              <ListAddress className="designation">Permission</ListAddress>
              {isSuperAdmin && <ListAddress className="action"></ListAddress>}
            </Listdiv>

            {admins.map((info: any, index: number) => (
              <Listdiv key={index}>
                <ListDate className="date">{getDate(info?.createdAt)}</ListDate>
                <ListDate className="text-wrap max-w-[150px]">
                  {info?.name}
                </ListDate>
                <ListDate className="email max-w-[240px] text-wrap">
                  {info?.email}
                </ListDate>
                <ListDate className="phone">{info?.phoneNumber}</ListDate>
                <ListDate className="role">{info?.role}</ListDate>
                <ListDate className="designation">
                  {info?.isSuperAdmin ? "Super Admin" : "Admin"}
                </ListDate>
                {isSuperAdmin && (
                  <Menu
                    as="div"
                    className="relative inline-block text-right w-[10%]"
                  >
                    <div>
                      <Menu.Button className="inline-flex justify-center w-8 h-8 rounded-full ">
                        {/* Three dots (ellipsis) icon */}
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                          className="w-6 h-6 text-gray-700 hover:text-gray-900"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M6.75 12a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0ZM12.75 12a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0ZM18.75 12a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0Z"
                          />
                        </svg>
                      </Menu.Button>
                    </div>

                    <Menu.Items className="absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white border border-black/20 ring-opacity-5 focus:outline-none z-50">
                      <div className="py-1">
                        {!info?.isSuperAdmin && (
                          <Menu.Item>
                            {({active}) => (
                              <button
                                onClick={() => {
                                  createSuperAdmin(info?.email);
                                }}
                                disabled={isSuperLoading}
                                className={`${
                                  active
                                    ? "bg-gray-100 text-gray-900"
                                    : "text-gray-700"
                                } flex px-4 py-2 text-sm w-full text-left font-semibold disabled:opacity-50`}
                              >
                                {isSuperLoading
                                  ? "Upgrading"
                                  : "Upgrade to Super Admin"}
                              </button>
                            )}
                          </Menu.Item>
                        )}
                        <Menu.Item>
                          {({active}) => (
                            <button
                              onClick={() => {
                                setAdminId(info?.id);
                                setIsModalOpen(true);
                                setModalState("delete");
                              }}
                              className={`${
                                active
                                  ? "bg-gray-100 text-gray-900"
                                  : "text-gray-700"
                              } flex px-4 py-2 text-sm w-full text-left`}
                            >
                              Delete Admin
                            </button>
                          )}
                        </Menu.Item>
                        <Menu.Item>
                          {({active}) => (
                            <button
                              onClick={() => {
                                setAdminEmail(info?.email);
                                setIsModalOpen(true);
                                setModalState("password");
                              }}
                              className={`${
                                active
                                  ? "bg-gray-100 text-gray-900"
                                  : "text-gray-700"
                              } flex px-4 py-2 text-sm w-full text-left`}
                            >
                              Reset Password
                            </button>
                          )}
                        </Menu.Item>
                      </div>
                    </Menu.Items>
                  </Menu>
                )}
              </Listdiv>
            ))}
          </HistoryList>
        ) : (
          <div className="py-5 border-gray-100 rounded">
            <EmptyState type="admin" text="No Admin user yet!" />
          </div>
        )}

        {/* New Modal Implementation */}
        <Transition appear show={isModalOpen} as={React.Fragment}>
          <Dialog
            as="div"
            className="relative z-50"
            onClose={() => setIsModalOpen(false)}
          >
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black/20 bg-opacity-25" />
            </Transition.Child>

            <div className="fixed inset-0 overflow-hidden">
              <div className="absolute inset-0 overflow-hidden flex items-center justify-center p-4">
                <div className="pointer-events-none flex max-w-[513px] h-max overflow-y-auto !rounded-[20px] overflow-hidden bg-white">
                  <Transition.Child
                    as={React.Fragment}
                    enter="transform transition ease-in-out duration-500 sm:duration-700"
                    enterFrom="translate-x-full"
                    enterTo="translate-x-0"
                    leave="transform transition ease-in-out duration-500 sm:duration-700"
                    leaveFrom="translate-x-0"
                    leaveTo="translate-x-full"
                  >
                    <Dialog.Panel className="pointer-events-auto w-screen max-w-md flex flex-col justify-between overflow-hidden">
                      <div className="!p-6">
                        {modalState === "delete" && (
                          <>
                            <p className="!text-xl !font-bold !text-[#272835] text-center">
                              Delete Admin
                            </p>
                            <p className="mt-2 !text-sm text-gray-400 text-center">
                              Are you sure you want to delete this admin?
                            </p>
                          </>
                        )}

                        {modalState === "password" && (
                          <>
                            <p className="!text-xl !font-bold !text-[#272835] text-center">
                              Reset Password
                            </p>
                            <p className="mt-2 !text-sm text-gray-400 text-center">
                              Are you sure you want to reset password for this
                              admin?
                            </p>
                          </>
                        )}
                        {modalState === "create" && (
                          <>
                            <p className="!text-xl !font-bold !text-[#272835] text-center">
                              Add New Admin
                            </p>
                            <div className="mt-4 space-y-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700">
                                  Name
                                </label>
                                <input
                                  type="text"
                                  name="name"
                                  onChange={handleChange}
                                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm p-2 border"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700">
                                  Password
                                </label>
                                <input
                                  type="password"
                                  name="password"
                                  onChange={handleChange}
                                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm p-2 border"
                                />
                                {data.password && (
                                  <div className="mt-2 space-y-1 flex flex-wrap gap-1">
                                    <PasswordRequirement
                                      met={data.password.length >= 8}
                                      text="At least 8 characters"
                                    />
                                    <PasswordRequirement
                                      met={/[A-Z]/.test(data.password)}
                                      text="At least one capital letter"
                                    />
                                    <PasswordRequirement
                                      met={/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(
                                        data.password
                                      )}
                                      text="At least one symbol"
                                    />
                                  </div>
                                )}
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700">
                                  Email
                                </label>
                                <input
                                  type="email"
                                  name="email"
                                  onChange={handleChange}
                                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm p-2 border"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700">
                                  Phone Number
                                </label>
                                <input
                                  type="tel"
                                  name="phoneNumber"
                                  onChange={handleChange}
                                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm p-2 border"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700">
                                  Role
                                </label>
                                <input
                                  type="text"
                                  name="role"
                                  onChange={handleChange}
                                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm p-2 border"
                                />
                              </div>
                            </div>
                          </>
                        )}
                        {modalState === "success" && (
                          <>
                            <div className="flex flex-col items-center">
                              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  className="h-8 w-8 text-green-600"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M5 13l4 4L19 7"
                                  />
                                </svg>
                              </div>
                              <p className="mt-4 text-lg font-medium text-gray-900">
                                Admin deleted successfully
                              </p>
                            </div>
                          </>
                        )}
                      </div>
                      <div className="flex items-center !gap-4 justify-between !p-6">
                        {modalState === "delete" && (
                          <>
                            <button
                              onClick={() => handleDelete()}
                              className="!bg-[#FE7F1F] !rounded-[10px] !font-bold !py-4 !px-[18px] !flex !items-center !justify-center !text-white w-1/2"
                            >
                              {deleteAdmin.isLoading
                                ? "Deleting..."
                                : "Yes, Delete"}
                            </button>
                            <button
                              onClick={() => setIsModalOpen(false)}
                              className="border !border-[#FF3B30] !rounded-[10px] !font-bold !py-4 !px-[41px] !flex !items-center !justify-center !text-[#FF3B30] w-1/2"
                            >
                              No
                            </button>
                          </>
                        )}
                        {modalState === "password" && (
                          <>
                            <button
                              onClick={() => resetPassword(adminEmail)}
                              className="!bg-[#FE7F1F] !rounded-[10px] !font-bold !py-4 !px-[18px] !flex !items-center !justify-center !text-white w-1/2"
                            >
                              {isResettingPass ? "Reseting..." : "Yes, Reset"}
                            </button>
                            <button
                              onClick={() => setIsModalOpen(false)}
                              className="border !border-[#FF3B30] !rounded-[10px] !font-bold !py-4 !px-[41px] !flex !items-center !justify-center !text-[#FF3B30] w-1/2"
                            >
                              No
                            </button>
                          </>
                        )}
                        {modalState === "create" && (
                          <>
                            <button
                              onClick={handleSubmit}
                              disabled={isLoading}
                              className="!bg-[#FE7F1F] !rounded-[10px] !font-bold !py-4 !px-[18px] !flex !items-center !justify-center !text-white w-full"
                            >
                              {isLoading ? "Creating..." : "Create"}
                            </button>
                          </>
                        )}
                        {modalState === "success" && (
                          <button
                            onClick={() => {
                              setIsModalOpen(false);
                              setModalState("default");
                            }}
                            className="!bg-[#FE7F1F] !rounded-[10px] !font-bold !py-4 !px-[18px] !flex !items-center !justify-center !text-white w-full"
                          >
                            Close
                          </button>
                        )}
                      </div>
                    </Dialog.Panel>
                  </Transition.Child>
                </div>
              </div>
            </div>
          </Dialog>
        </Transition>

        <Feedback
          feedback={feedback}
          open={openFeedback}
          close={() => setOpenFeedback(false)}
        />
      </div>
    </AppLayout>
  );
};

const CreateAdmin = styled.button`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 18px 18px;
  background: #f66d00;
  border-radius: 5px;
  border: none;
  color: #ffffff;
  font-weight: 800;
`;

const HistoryList = styled.section`
  width: 100%;
  @media (max-width: 425px) {
    width: 100%;
    background-color: #ffffff;
  }
`;

const Listdiv = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 95%;
  height: 50px;
  margin: 16px auto;
  padding: 0px 20px;
  background-color: #ffffff;
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.01);
  border-bottom: 1px solid #e0e0e0;
  @media (max-width: 425px) {
    width: 90%;
  }
`;
const ListAddress = styled.p`
  font-weight: 300;
  color: #000000;
  font-size: 16px;
  font-weight: 700;
  margin: 0;
  text-align: left;
  width: 15%;
  &.action {
    text-align: right;
    width: 10%;
  }
  &.date {
    width: 10%;
  }
  &.designation {
    width: 10%;
  }
  &.email {
    width: 25%;
  }
`;

const ListDate = styled.p`
  color: #333;
  font-weight: 400;
  font-size: 14px;
  margin: 0;
  text-align: left;
  width: 15%;
  &.action {
    text-align: right;
    width: 10%;
    cursor: pointer;
  }
  &.date {
    width: 10%;
  }
  &.designation {
    width: 10%;
    font-weight: 500;
  }
  &.email {
    width: 25%;
  }
`;

const PasswordRequirement: React.FC<{met: boolean; text: string}> = ({
  met,
  text,
}) => (
  <div className="flex items-center gap-2">
    <div
      className={`w-3 h-3 rounded-full flex items-center justify-center ${
        met ? "bg-green-500" : "bg-gray-300"
      }`}
    >
      {met && (
        <svg
          className="w-2 h-2 text-white"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={4}
            d="M5 13l4 4L19 7"
          />
        </svg>
      )}
    </div>
    <span
      className={`text-xs ${met ? "text-green-600" : "text-gray-500 w-max"}`}
    >
      {text}
    </span>
  </div>
);
